import { Button, Card, Form, Row, Select, Upload, Alert, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { SRSApplicantType } from "utils/Types";
import { utasRatingScores } from "utils/AssetsData";
import { convertRatingObjToArray, deepEqual, excludeEmptyObj } from "utils/hooks/handleObjAndArray";
import { requiredRule } from "utils/validators/rules";
import { useTranslation } from "react-i18next";
import TextArea from "antd/lib/input/TextArea";
import api from "ApiClient";
import { ApiURL, AppURL } from "utils/Actions";
import { API_RESPONSE_STATUS } from "utils/Constants";
import { addRatedStudent, setScoreAndValue } from "redux/reducers/RatingsSlice";
import { showErrorNotification, showSuccessNotification } from "utils/hooks/handleNotification";
import FileItem from "components/common/file-item";
import { validateFile } from "utils/hooks/documentActions";
import { createPutRequest } from "utils/hooks/apiActions";
import { fetchYear11Qual } from "redux/reducers/Year11QualSlice";
import { fetchComment, setComment as setReduxComment } from "redux/reducers/CommentSlice";
import { fetchDocuments, setDocuments } from "redux/reducers/DocumentSlice";
import SubmitModal from "pages/school-user/ratings/modals/SubmitModal";
import CustomPrompt from "components/common/custom-prompt";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import paperClipGrey from "assets/icons/paper-clip-grey.svg";
import style from "./style.module.css";

interface UTASRatingFormProps {
  handleError: (error: any) => void;
  backToApplicationTable: () => void;
}
const isPrintableASCII = (input: string) => /^[\x20-\x7F]*$/.test(input);

export default function UTASRatingForm({
  handleError,
  backToApplicationTable
}: UTASRatingFormProps) {
  const dispatch = useAppDispatch();
  const [registerForm] = Form.useForm();
  const { t } = useTranslation();
  const history = useHistory();

  const { refNum: studentRefNumParam } = useParams<{ refNum: string }>();

  const { tableData: tableDataInRater } = useAppSelector(state => state.srsApplicants);
  const { data: year11Qual, isLoadingError: year11QualError } = useAppSelector(
    state => state.year11Qual
  );
  const {
    data: initialComment,
    isLoadingError: commentLoadingError,
    isLoading: commentLoading
  } = useAppSelector(state => state.comments);
  const {
    data: previousDocuments,
    isLoadingError: documentsLoadingError,
    isLoading: documentsLoading
  } = useAppSelector(state => state.documents);
  const lastComment = initialComment.length ? initialComment[0].comment : "";

  const { topics, initialValue } = useAppSelector(state => state.ratings);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [ratingsUnchanged, setRatingsUnchanged] = useState(true);
  const selectedStudent = tableDataInRater.find(
    (e: SRSApplicantType) => e.refNum.toString() === studentRefNumParam
  );
  const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [comment, setComment] = useState<string>("");
  const [commentError, setCommentError] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  // for setting required rule on form items
  const [isSubmitRule, setIsSubmitRule] = useState(false);

  const removeValidationErrors = (error: any) => {
    setValidationErrors((prev: any[]) => {
      return prev.filter(e => e.id != error.id);
    });
  };

  // cleanup
  useEffect(() => {
    return () => {
      dispatch(setDocuments([]));
      dispatch(setReduxComment([]));
    };
  }, []);

  useEffect(() => {
    selectedStudent && dispatch(fetchYear11Qual(selectedStudent.refNum));
    selectedStudent && dispatch(fetchComment(selectedStudent.refNum));
  }, [selectedStudent?.refNum]);

  useEffect(() => {
    if (selectedStudent && year11Qual.number) {
      dispatch(
        fetchDocuments({
          refNum: selectedStudent.refNum,
          qualNum: year11Qual.number
        })
      );
    }
  }, [selectedStudent?.refNum, year11Qual.number]);

  useEffect(() => {
    setComment(lastComment);
  }, [lastComment]);

  const onFieldsChange = useCallback(() => {
    const values = registerForm.getFieldsValue();
    const cleanedValues = excludeEmptyObj(values);
    const resultDeepEqual = deepEqual(cleanedValues, initialValue);
    setRatingsUnchanged(resultDeepEqual);
  }, []);

  const handleCommentChange = useCallback(e => {
    const { value } = e.target;
    const passes = isPrintableASCII(value);
    if (passes) {
      setComment(value);
      setCommentError(false);
    } else {
      setCommentError(true);
    }
  }, []);

  const handleClickSubmitButton = useCallback(async () => {
    setIsSubmitRule(true);
    const valid = await registerForm.validateFields().then(values => {
      const isPopulated = Object.values(values).every(item => !isNil(item));
      if (isPopulated) {
        return true;
      } else {
        return false;
      }
    });
    if (valid) {
      setIsSubmitModalOpen(true);
    }
  }, []);

  const onSubmit = useCallback(
    async (isFinalSubmission: boolean) => {
      if (!selectedStudent) {
        showErrorNotification({ message: "Could not save. Please close and try again." });
        return false;
      }
      setRatingsUnchanged(true);
      setIsSubmitRule(isFinalSubmission);
      isFinalSubmission ? setIsSubmitting(true) : setIsDraftSubmitting(true);

      if (comment !== lastComment) {
        try {
          const commentRes = await createPutRequest({
            path: ApiURL.raterComment,
            body: {
              refNumber: year11Qual.refNum,
              comment: comment
            }
          });
          if (commentRes.data.status !== API_RESPONSE_STATUS.SUCCESS) {
            showErrorNotification({ message: "Could not save comment. Please try again." });
            return false;
          }
        } catch (error: any) {
          handleError(error);
          return false;
        }
      }

      if (!isEmpty(fileList)) {
        const filePromises = Promise.all(
          fileList.map(async file => {
            const formData = new FormData();
            formData.append("file", file);
            const res = await api.post(
              `${ApiURL.uploadDocument}/${year11Qual.refNum}/${year11Qual.number}`,
              formData
            );
            return res;
          })
        );

        try {
          const filePromiseResults = await filePromises;
          filePromiseResults.forEach((result: any, i: any) => {
            if (result.status !== 201) {
              const msg = `${fileList[i].name} could not be submitted.`;
              showErrorNotification({
                message: msg
              });
              return false;
            }
          });
        } catch (error: any) {
          handleError(error);
          return false;
        }
      }

      registerForm.validateFields().then(async values => {
        const scores = convertRatingObjToArray(values);
        const newScoreBody = {
          ratings: {
            refNum: Number(selectedStudent.refNum),
            draft: !isFinalSubmission,
            qualRatings: scores || []
          }
        };
        try {
          const res = await api.post(ApiURL.ratingScore, newScoreBody);
          const { status } = await res?.data;
          if (status === API_RESPONSE_STATUS.SUCCESS) {
            dispatch(setScoreAndValue(newScoreBody.ratings.qualRatings));
            setRatingsUnchanged(true);
            if (isFinalSubmission) {
              dispatch(addRatedStudent(selectedStudent));
            } else {
              showSuccessNotification({ message: "Rating saved" });
            }
            backToApplicationTable();
          }
        } catch (error: any) {
          handleError(error);
          return false;
        } finally {
          setIsDraftSubmitting(false);
          setIsSubmitting(false);
        }
      });
    },
    [comment, fileList, selectedStudent?.refNum]
  );

  const removeFile = useCallback((file: any) => {
    setFileList((prev: any[]) => {
      return prev.filter(e => e != file);
    });
  }, []);

  const updateValidationErrors = (id: string, msg: React.ReactNode) => {
    setValidationErrors((prev: any[]) => {
      return [...prev, { id, msg }];
    });
  };

  const uploadDocumentProps = {
    id: "documents",
    name: "documents",
    multiple: true,
    fileList: isEmpty(fileList)
      ? []
      : [
          {
            label: true,
            name: t("rating.documents.uploaderCurrently")
          },
          ...fileList
        ],
    beforeUpload: (file: any) => {
      const { valid, errors } = validateFile(file);
      if (valid) {
        setValidationErrors([]);
        setFileList((prev: any[]) => [...prev, file]);
      } else {
        errors.forEach(({ id, msg }) => {
          updateValidationErrors(id, msg);
        });
      }
      return false;
    },
    itemRender: (originNode: any, file: any) => <FileItem file={file} removeFile={removeFile} />
  };

  if (year11QualError || commentLoadingError || documentsLoadingError) {
    return (
      <div>
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 20 }}
          message={t("common.apiFailWithCTA")}
        />
      </div>
    );
  }

  const stopNavigation = useMemo(() => {
    const shouldStopNavigation =
      (!ratingsUnchanged || comment !== lastComment || !isEmpty(fileList)) &&
      !isSubmitting &&
      !isDraftSubmitting;
    return shouldStopNavigation;
  }, [ratingsUnchanged, comment, lastComment, fileList, isSubmitting, isDraftSubmitting]);

  const disableDraftButton = useMemo(() => {
    const disableButton =
      ratingsUnchanged &&
      comment === lastComment &&
      isEmpty(fileList) &&
      !isSubmitting &&
      !isDraftSubmitting;
    return disableButton;
  }, [ratingsUnchanged, comment, lastComment, fileList, isSubmitting, isDraftSubmitting]);

  const handleSaveChanges = useCallback(async () => {
    await onSubmit(false);
  }, [onSubmit]);

  return (
    <>
      <SubmitModal
        isopen={isSubmitModalOpen}
        onclose={() => setIsSubmitModalOpen(false)}
        backToApplication={backToApplicationTable}
        handleSubmit={() => onSubmit(true)}
        isSubmitting={isSubmitting}
        isSubmitError={false}
      />

      <CustomPrompt
        when={stopNavigation}
        navigate={path => history.push(path)}
        shouldBlockNavigation={location => {
          if (stopNavigation) {
            if (!location.toString().startsWith(AppURL.school.rater.ratings)) {
              return true;
            }
          }
          return false;
        }}
        onSaveChanges={handleSaveChanges}
      />
      <Form
        name="rating-form"
        className="rating-form"
        form={registerForm}
        initialValues={initialValue}
        onFieldsChange={onFieldsChange}
        layout="vertical"
        scrollToFirstError={true}
      >
        {topics.map((level: any, i: number) => {
          return (
            <Card style={{ marginTop: "2rem" }} key={level.elementId}>
              <fieldset role="radiogroup" aria-labelledby={`group-label-${i}`}>
                <Form.Item
                  label={
                    <legend id={`group-label-${i}`} className={style["radio-label"]}>
                      {level.topic}
                    </legend>
                  }
                  name={level.elementId}
                  required
                  rules={isSubmitRule ? [requiredRule] : []}
                >
                  <Select placeholder="Select..." options={utasRatingScores} />
                </Form.Item>
              </fieldset>
            </Card>
          );
        })}

        <Card style={{ marginTop: "2rem" }}>
          {/* is not a part of the form, and submitted to the api separately */}

          <label htmlFor="comment">
            <div className={style["radio-label"]}>{t("rating.comment.label")}</div>
            <p>{t("rating.comment.description")}</p>
          </label>
          {commentLoading ? (
            <Spin />
          ) : (
            <TextArea
              id="comment"
              showCount
              maxLength={500}
              style={{ height: 136 }}
              value={comment}
              onChange={handleCommentChange}
            />
          )}
          {commentError && (
            <div style={{ color: "#D72C0D" }}>Only alphanumeric characters allowed.</div>
          )}
        </Card>

        <Card style={{ marginTop: "2rem" }}>
          {/* is not a part of the form, and submitted to the api separately */}
          <label htmlFor="documents">
            <div className={style["radio-label"]}>{t("rating.documents.label")}</div>
            <p>{t("rating.documents.description")}</p>
          </label>
          {documentsLoading && <Spin />}
          {!isEmpty(previousDocuments) && (
            <div className={style["previous-documents-container"]}>
              <div style={{ marginBottom: 4 }}>{t("rating.documents.previousDocumentLabel")}</div>
              <ul className={style["previous-documents-list"]}>
                {previousDocuments.map((pd, i) => (
                  <li key={`${pd}-${i}`} className={style["previous-document-item"]}>
                    <img src={paperClipGrey} alt="" />
                    &nbsp;&nbsp;
                    <div>{pd}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {validationErrors.length > 0 &&
            validationErrors.map((error: any) => (
              <Alert
                key={error.id}
                type="error"
                message={error.msg}
                showIcon
                closable={true}
                className={style["error-alert"]}
                onClose={() => removeValidationErrors(error)}
              />
            ))}
          <Upload.Dragger {...uploadDocumentProps}>
            <p className={style["upload-instruction"]}>{t("rating.documents.uploaderLabel")}</p>
            <p className={style["upload-note"]}>{t("rating.documents.uploaderDescription")}</p>
          </Upload.Dragger>
        </Card>

        <Row style={{ marginTop: "3rem" }}>
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginRight: "2rem", marginBottom: "2rem" }}
            onClick={handleClickSubmitButton}
          >
            {t("rating.submitBtn")}
          </Button>
          <Button
            loading={isDraftSubmitting}
            onClick={() => onSubmit(false)}
            disabled={disableDraftButton}
          >
            {t("rating.saveBtn")}
          </Button>
        </Row>
      </Form>
    </>
  );
}
